/** ***********
*
* <app.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { catchError, distinctUntilChanged, filter, map, retry, shareReplay, skipUntil, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, Renderer2, SecurityContext } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ActivatedRouteSnapshot, Data, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, from, Observable, of, race, Subscription } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { ShowConditionsOfUseComponent } from './components/shared/show-conditions-of-use/show-conditions-of-use.component';
import { ShowUserNoRightsComponent } from './components/shared/show-user-no-rights/show-user-no-rights.component';
import { UserService } from './services/user.service';
import { fetchAllAssets } from './store/actions/asset.actions';
import { fetchAllFamilies } from './store/actions/family.action';
import { fetchAllParentFamilies } from './store/actions/parent-family.action';
import { fetchAllCompanies } from './store/actions/company.actions';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { fetchAllSites } from './store/actions/site.action';
import { fetchAllTrackers } from './store/actions/tracker.action';
import { successFetchAllProfiles } from './store/actions/profile.action';
import { fetchAllMasterData } from './store/actions/masterdata.action';
import { WebSocketService } from './services/websocket.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { fetchAllZones } from './store/actions/zone.action';
import { Profile } from './models/profile';
import { ContextService } from './services/init-filter.service';
import { contextIsValid$, selectUserSelectedContext$ } from './store/selectors/filter-context.selectors';
import { selectParentFamilyLoaded$ } from './store/selectors/parent-family.selectors';
import { selectFamilyLoaded$ } from './store/selectors/family.selectors';
import { selectAssetLoaded$ } from './store/selectors/asset.selectors';
import { selectTrackerLoaded$ } from './store/selectors/tracker.selectors';
import { LoadFamilyRight } from './store/actions/familyRight.action';
import { isEqual, pick } from 'lodash';
import { DEFAULT_DIALOG_OPTIONS, DialogConfigureContextComponent } from './components/init-filter/configure-context/dialog-configure-context.component';
import { ConfigurationStorageKeys, LocalStorageService } from './services/local-storage.service';
import { selectProfileState$ } from './store/selectors/profile.selectors';
import { CacheService } from './services/cache.service';
import { selectZoneState$ } from './store/selectors/zone.selector';
import { successFiltersAdminZones } from './store/actions/filter.actions';
import { LoggerService } from './services/logger.service';
import { LoadParentFamilyRight } from './store/actions/user-right.action';
import { PosthogService } from './services/posthog.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    @HostBinding('class.mobile') get mobileClass() {
        return this._mobile;
    }

    public loader = false;
    public showFilterContext$ = this.router.events.pipe(
        filter<NavigationEnd>(event => event instanceof NavigationEnd),
        switchMap((): Observable<Data|null> => {
            const getCombinedChildData = (child: ActivatedRoute): Observable<Data> => {
                if (child.children.length) {
                    return combineLatest([
                        child.data,
                        getCombinedChildData(child.children[0]),
                    ]).pipe(
                        map(([data, childData]) => ({
                            ...data,
                            ...childData,
                        })),
                    );
                }
                return child.data;
            };
            const lastChildData = getCombinedChildData(this.activatedRoute);
            return lastChildData ?? of(null);
        }),
        catchError(() => of(null)),
        map(data => data?.context),
        tap((v) => {
            // Close context dialog if no context is required
            if (!v) {
                this._initFilterdataService.contextConfigDialogRef?.close();
            }
        }),
        shareReplay(1),
    );
    private subscription: Subscription = new Subscription();
    private _startScreenSessionTime: number;
    private _routeBeforeNavigation: string;
    private _mobile: boolean;

    constructor(
        private store: Store,
        private userService: UserService,
        private auth: AuthService,
        private router: Router,
        private _breadkpointObserver: BreakpointObserver,
        public dialog: MatDialog,
        private _titleService: Title,
        private _webSocketService: WebSocketService,
        private _localStorageService: LocalStorageService,
        private _cacheService: CacheService,
        private activatedRoute: ActivatedRoute,
        private _initFilterdataService: ContextService,
        public _breakpointObserver: BreakpointObserver,
        private _cd: ChangeDetectorRef,
        private _sanitize: DomSanitizer,
        private _renderer: Renderer2,
        private _loggerService: LoggerService,
        private _posthogService: PosthogService // Inject the service in your app's root component will make sure PostHog is initialized before any other component is rendered.
    ) {
        // Store the value of the store in a global variable (not in production)
        // if (environment.envName !== 'production') {
        //     store.subscribe(storeValue => window['_store'] = { ...storeValue });
        // }
         
        console.info(`You are connected to %c${environment.envName} %cat %c${environment.API_ENDPOINT}`, 'color: #ffc107;', 'color: #fff', 'color: #ffc107;');
        // This has to be in constructor to manage first route event
        this.subscription.add(this.router.events.pipe(
            filter(e => e instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (this._routeBeforeNavigation && event.urlAfterRedirects.split('?')[0] !== this._routeBeforeNavigation) {
                    // const attribute: number = Math.round(((performance.now() - this._startScreenSessionTime) / 1000) * 1e2) / 1e2;
                    // const message = attribute + 's passed on route ' + this._routeBeforeNavigation;
                    this._startScreenSessionTime = performance.now();
                }
                this._routeBeforeNavigation = event.urlAfterRedirects.split('?')[0];
            }));
    }

    ngOnInit() {
        this.subscription.add(
            this._breakpointObserver.observe([
                Breakpoints.Handset,
            ]).subscribe(
                ({ matches }) => {
                    this._mobile = matches;
                    this._cd.markForCheck();
                }
            )
        )

        const title = environment.production ? 'WAY' : 'WAY | ' + environment.envName.toUpperCase();
        this._activateGuideMePlugin();
        this._titleService.setTitle(title);
        this._startScreenSessionTime = performance.now();

        this.subscription.add(
            this.auth.tokenValid.pipe(
                filter((isValid) => isValid),
                tap(() => this.loader = true),
                take(1),
                switchMap(() => combineLatest([
                    this.userService.getProfiles(),
                    this.auth.acceptConditions,
                    from(this._cacheService.initializeDB()),
                ])),
                tap(([profile, accept]) => {
                    if (!accept) {
                        if (profile.is_first_login) {
                            this.userService.setUserFirstTime().subscribe();
                            // Delete database
                            this._cacheService.initializeDB(true).then(
                                () => this._loggerService.debug('AppComponent', 'Cache database successfuly deleted')
                            ).catch(
                                error => this._loggerService.error('AppComponent', 'Cannot delete the cache database', error)
                            );
                        }
                        if (!profile.validation) {
                            this.loader = false;
                            this.dialog.open(ShowConditionsOfUseComponent, {
                                width: 'fit-content',
                                autoFocus: false,
                                disableClose: true,
                                data: { validation: false },
                            });
                            return;
                        }
                        this.auth.setAccept(true);
                    }
                }),
                filter(([, accept]) => accept),
                take(1)
            ).subscribe(([profile]) => {
                this.loader = false;
                const { parentFamilies } = profile;
                const getPathRecursively = ({ routeConfig, firstChild }: ActivatedRouteSnapshot): string => {
                    const path = routeConfig?.path ?? '';
                    if (firstChild) {
                        return `${path}/${getPathRecursively(firstChild)}`;
                    }
                    return path;
                };
                const path = getPathRecursively(this.activatedRoute.snapshot);
                if (!parentFamilies.length && path !== '/rights/request') {
                    this.subscription.unsubscribe();
                    const dialogRef = this.dialog.open(ShowUserNoRightsComponent, {
                        autoFocus: false,
                        disableClose: true,
                        maxWidth: '100%',
                    });
                    this._breadkpointObserver
                        .observe([Breakpoints.HandsetPortrait, Breakpoints.XSmall])
                        .subscribe(
                            (state) => {
                                if (state.matches) {
                                    dialogRef.updateSize('100%', '100%');
                                } else {
                                    dialogRef.updateSize('fit-content', 'auto');
                                }
                            }
                        );
                    dialogRef.afterClosed().subscribe(() => {
                        this._handleProfileFetched(profile);
                    });
                } else {
                    this._handleProfileFetched(profile);
                }
            })
        );

        this.subscription.add(
            this._localStorageService.storageValue$.pipe(
                filter(({ key, value }) => key == ConfigurationStorageKeys.wcagSFI && !!value),
            ).subscribe(
                ({ value }) => {
                    document.querySelector('body').classList.toggle('wcag-focus-indicators', JSON.parse(value));
                }
            )
        );

        this._localStorageService.fetchLocalStorageValues$().subscribe();
    }

    private _handleProfileFetched(profile: Profile) {
        // Set the profile
        this.store.dispatch(successFetchAllProfiles({ profile: profile }));
        // Fetch base data for pages where context selection is not required
        this.store.dispatch(fetchAllCompanies());
        this.store.dispatch(fetchAllSites());
        // Listen to context
        const contextSelectionChange$ = this.store.select(selectUserSelectedContext$).pipe(
            // Check if the selection has changed
            distinctUntilChanged((previous, current) => isEqual(
                pick(previous, ['companies', 'sites', 'parentFamilies', 'families', 'providers']),
                pick(current, ['companies', 'sites', 'parentFamilies', 'families', 'providers'])
            )),
            switchMap(() => this.store.select(contextIsValid$)),
            filter(valid => valid),
        );
        const hasRights$ = this.store.select(selectProfileState$).pipe(
            filter(state => state.loaded),
            map(state => state.data.profiles.parent_families.length > 0),
            distinctUntilChanged(),
        );

        // On first context selection, load the services which are not dependent on context
        // Ensure the user has rights before fetching data
        this.subscription.add(
            hasRights$.pipe(
                filter(hasRights => hasRights),
                switchMap(() => race([
                    this.showFilterContext$.pipe(
                        filter(v => !v),
                    ),
                    contextSelectionChange$,
                ])),
                take(1),
            ).subscribe(
                () => {
                    this._loadServicesOutOfContext();
                }
            )
        );

        this.subscription.add(
            // Ensure the user has rights before fetching data
            hasRights$.pipe(
                filter(hasRights => hasRights),
                take(1),
                switchMap(() => contextSelectionChange$),
            ).subscribe(
                () => this._loadServicesWithContext()
            )
        );

        // #region Context becomes invalid
        this.subscription.add(
            this.store.select(contextIsValid$).pipe(
                // Wait for context to be valid
                filter(valid => valid),
                switchMap(() => this.store.select(contextIsValid$).pipe(
                    filter(valid => !valid),
                    take(1),
                )),
                switchMap(() => this.store.select(selectUserSelectedContext$)),
            ).subscribe(
                (context) => this.dialog.open(
                    DialogConfigureContextComponent,
                    {
                        ...DEFAULT_DIALOG_OPTIONS,
                        hasBackdrop: false,
                        disableClose: true,
                        data: context,
                    }
                )
            )
        );
        // #endregion
    }

    /**
     * Strategy: Load all the essential data of the application when loading the app
     */
    private _loadServicesOutOfContext() {
        // inform the content loader service that the app is ready to request content urls (for user guides)
        this.store.dispatch(fetchAllMasterData());
        this.store.dispatch(fetchAllZones());

        this.subscription.add(
            this.store.select(selectZoneState$).pipe(
                filter(zoneState => zoneState.loaded),
                take(1)
            ).subscribe((zones) => {
                this.store.dispatch(successFiltersAdminZones({ zones: zones.data }));
            })
        );

        // right requests and parent families rights
        this.store.dispatch(new LoadFamilyRight());

        // WebSocket connection
        this._webSocketService.connect();
    }
    /**
     * Strategy: Load all the data of the application which are dependent on a context selection
     */
    private _loadServicesWithContext() {
        this.store.dispatch(fetchAllParentFamilies());
        this.store.dispatch(fetchAllFamilies());
        this.store.dispatch(fetchAllAssets());
        this.store.dispatch(fetchAllTrackers());
        this.loader = true;
        setTimeout(() => {
            combineLatest([
                this.store.select(selectParentFamilyLoaded$),
                this.store.select(selectFamilyLoaded$),
                this.store.select(selectAssetLoaded$),
                this.store.select(selectTrackerLoaded$),
            ]).pipe(
                filter(loadStatus => loadStatus.every(s => !!s)),
                take(1),
            ).subscribe(
                () => {
                    this.loader = false;
                    this._cd.detectChanges();
                }
            );
        });
    }

    /**
     * Activate or note guide me plugin depending on environment
     */
    private _activateGuideMePlugin() {
        if (document.body.tagName !== 'FRAMESET' && environment.guideMeActive) {
            const scriptElement = this._renderer.createElement("script") as HTMLScriptElement;
            this._renderer.setAttribute(scriptElement, 'id', 'KNOW_TAG_ID');
            this._renderer.setAttribute(scriptElement, 'src', this._sanitize.sanitize(SecurityContext.URL, environment.guideMeUrl))
            if (document.head) {
                this._renderer.appendChild(document.head, scriptElement);
            } else {
                this._renderer.appendChild(document.body, scriptElement);
            }
        }
    }

    ngAfterViewInit() {
        if (location.pathname !== '/') { return; }
        this.router.navigate(['map/view-map']);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
